import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import MuiTextFieldText from '../../../mui/MuiTextField/MuiTextFieldText/MuiTextFieldText';
import {
	AddressFieldSet,
	AddressFieldsProps,
} from '../../../mui/AddressFieldSet';
import styles from './YourBusiness.module.scss';
import AvatarSelector, { AvatarSelectorValues } from '../../AvatarSelector';
import AvatarSelected from '../../AvatarSelected';
import { BusinessTypes } from './helpers';
import MuiPhoneInput from '../../../mui/MuiPhoneInput';
import SelectServices from '../../SelectServices';
import { Country } from '#types';
import { BusinessStructure } from './BusinessStructure';
import { TermsOfService, TermsOfServiceProps } from '../../TermsOfService';

export type YourBusinessProps = {
	businessName?: string;
	setBusinessName: React.Dispatch<React.SetStateAction<string | undefined>>;
	businessNameValidation?: string;
	businessType?: BusinessTypes;
	setBusinessType: React.Dispatch<
		React.SetStateAction<BusinessTypes | undefined>
	>;
	businessTypeValidation?: string;
	selectedServices?: { name: string; id: string }[] | undefined | null;
	setSelectedServices: React.Dispatch<
		React.SetStateAction<{ name: string; id: string }[] | undefined | null>
	>;
	selectedServicesValidation?: string;
	address?: AddressFieldsProps['address'];
	setAddress: React.Dispatch<AddressFieldsProps['address']>;
	addressValidation?: AddressFieldsProps['validate'];
	phone?: string;
	setPhone: React.Dispatch<React.SetStateAction<string | undefined>>;
	phoneValidation?: string;
	image?: AvatarSelectorValues | string;
	setImage: React.Dispatch<
		React.SetStateAction<AvatarSelectorValues | string | undefined>
	>;
} & TermsOfServiceProps;

export const YourBusiness: React.FC<YourBusinessProps> = ( {
	businessName,
	setBusinessName,
	businessNameValidation,
	businessType,
	setBusinessType,
	businessTypeValidation,
	selectedServices,
	setSelectedServices,
	selectedServicesValidation,
	address,
	setAddress,
	addressValidation,
	phone,
	setPhone,
	phoneValidation,
	image,
	setImage,
	termsOfServiceAccepted,
	setTermsOfServiceAccepted,
	termsOfServiceAcceptedValidation,
	consentedToAgeAndMarketingEmails,
	setConsentedToAgeAndMarketingEmails,
	consentedToAgeAndMarketingEmailsValidation,
	onBlur,
} ) => {
	const phoneCountry = useMemo( () => {
		return address?.country === Country.CA ? 'ca' : 'us';
	}, [ address ] );

	return (
		<>
			<Typography variant='h1' component='h1'>
				Let's create your business profile
			</Typography>
			<Box className={ styles.form }>
				<Box className={ styles.row }>
					<MuiTextFieldText
						fullWidth
						label='Business name'
						autoComplete='organization'
						variant='standard'
						value={ businessName || '' }
						onChange={ ( e ) => setBusinessName( e.target.value ) }
						errorText={ businessNameValidation }
						onBlur={ onBlur }
						slotProps={ {
							htmlInput: {
								'data-cy': 'businessName',
							},
						} }
						autoFocus
					/>
					<BusinessStructure
						businessType={ businessType }
						setBusinessType={ setBusinessType }
						businessTypeValidation={ businessTypeValidation }
						onBlur={ onBlur }
					/>
				</Box>
				<SelectServices
					errorText={ selectedServicesValidation }
					variant='outlined'
					fullWidth
					value={ selectedServices }
					onChange={ ( selectedServices ) => setSelectedServices( selectedServices ) }
					onBlur={ onBlur }
					dataCy='selectedServices'
					chipLimitDesktop={ 3 }
					chipLimitMobile={ 1 }
				/>
				<AddressFieldSet
					address={ address || {} }
					onChange={ ( values ) => setAddress( values ) }
					validate={ addressValidation }
					onBlur={ onBlur }
				/>
				<Box className={ styles.row }>
					<MuiPhoneInput
						fullWidth
						variant='standard'
						label='Phone number'
						autoComplete='tel'
						value={ phone || '' }
						onChange={ setPhone }
						errorText={ phoneValidation }
						onBlur={ onBlur }
						slotProps={ {
							htmlInput: {
								'data-cy': 'phone',
							},
						} }
						country={ phoneCountry }
					/>
					{ image ? (
						<AvatarSelected
							label='Business logo or image (optional)'
							value={
								typeof image === 'string'
									? image
									: image.originalImage instanceof File
										? image.originalImage.name
										: image.originalImage
							}
							onRemove={ () => setImage( undefined ) }
						/>
					) : (
						<AvatarSelector
							onSelect={ ( selection ) => setImage( selection ) }
							label='Business logo or image (optional)'
						/>
					) }
				</Box>
				<TermsOfService
					includeConnectStatement
					consentedToAgeAndMarketingEmails={ consentedToAgeAndMarketingEmails }
					setConsentedToAgeAndMarketingEmails={
						setConsentedToAgeAndMarketingEmails
					}
					consentedToAgeAndMarketingEmailsValidation={
						consentedToAgeAndMarketingEmailsValidation
					}
					termsOfServiceAccepted={ termsOfServiceAccepted }
					setTermsOfServiceAccepted={ setTermsOfServiceAccepted }
					termsOfServiceAcceptedValidation={ termsOfServiceAcceptedValidation }
					onBlur={ onBlur }
				/>
			</Box>
		</>
	);
};
